import React, { useCallback, useEffect, useRef, useState } from "react";
import { PATIENT_LAST_NAME_OR_MRN_MAX_LENGTH, QUICK_ENTRY_FIELD_MAX_LENGTH } from "./quickEntryFieldConstraints";
import { FieldGroupRow, FieldGroupRowStatus, FieldGrounding } from "../../../models/intake";
import QuickEntryGroundingIcon from "./QuickEntryGroundingIcon";
import {
    DEFAULT_PATIENT_MATCH_STATE,
    PatientMatchApiPayload,
    PatientMatchState,
    mapPatientMatchState,
    resolvePatientMatchWriteKey,
    toDisplayDob,
    toWireDob,
    buildPatientMatchTooltip,
    flattenMatchTooltip,
} from "../matchState";
import MatchTooltipBody, { MatchInfoTooltip } from "./MatchTooltipBody";
import InlineSearchForm from "./InlineSearchForm";
import MatchGridModal, { MatchGridColumn, MatchGridRow } from "./MatchGridModal";
import CreateEntityFormModal, { CreateEntityField } from "./CreateEntityFormModal";
import { shouldPrefillCreateForm } from "./createPrefill";
import "./PatientInfoRow.scss";

export const PATIENT_INFO_ROW_ID = "patientInfo";

export interface PatientSearchKeys {
    readonly lastName?: string;
    readonly dob?: string;
    readonly mrn?: string;
}

/** Create-new patient values captured by the no-match create form (AC9) - the same fields classic's add-new-patient form captures. */
export interface PatientCreateValues {
    readonly firstName: string;
    readonly middleName: string;
    readonly lastName: string;
    readonly dob: string;
    readonly mrn: string;
    readonly empi: string;
}

export interface PatientInfoRowProps {
    /**
     * AI-extracted keys from redux `requestAnalysis` (F1a). Undefined/empty
     * until extraction completes (AC13) - no keys means no search runs
     * (Business Rule 0/1).
     */
    readonly extractedLastName?: string;
    /** AI-extracted patient given (first) name - pre-fills the create-new form (not used for the search, which keys on lastName+dob). */
    readonly extractedFirstName?: string;
    readonly extractedDob?: string;
    /**
     * Calls the stateless POST /patient-search endpoint (AC1/AC4) - the SAME
     * function serves the automated match (this component calls it with the
     * extracted keys once they're available) and the INLINE re-search (called
     * again with specialist-typed keys from the row's inline fields).
     */
    readonly search: (keys: PatientSearchKeys) => Promise<PatientMatchApiPayload>;
    /**
     * "Create New" (AC9 no-match path): the specialist filled the create-new
     * form (pre-filled with the AI-extracted values when no DB match was found -
     * AB#38824, createPrefill.ts - then edited). The values
     * are written into the data-entry form by the parent - this component does
     * NOT persist to the database; the transaction's final Save persists them.
     */
    readonly onCreatePatient?: (values: PatientCreateValues) => void;
    /**
     * AC9 resolved-existing-match path (F1c coalesce 2026-07-20 fix): fired when
     * the row resolves to an EXISTING patient - either an automated single
     * (green) match or a grid pick from a Multiple result. Carries the resolved
     * patient's identity so the parent writes it into DATA_ENTRY_FIELDS_FORM,
     * exactly as onCreatePatient does for the create-new path. Before this, a
     * picked/auto-matched existing patient lived only in this row's local
     * apiResult and was lost when the row unmounted (e.g. moving to the Billing
     * step), so the committed transaction never carried the matched patient.
     * NOT fired for create-new (onCreatePatient owns that) - the write-once
     * effect that raises this is guarded on `!created`.
     */
    readonly onSelectPatient?: (values: PatientCreateValues, patientId?: number) => void;
    /**
     * Reports this row's computed match state UP to the parent whenever it
     * changes, so the parent (DataEntryDetails.getQuickEntryRows) can drive the
     * Quick Entry numbered-list BADGE icon from the REAL match result. Without
     * this the parent only had a placeholder (isSearching hardcoded true) and
     * the badge was stuck on amber/LowConfidence once keys existed. Fired from
     * a useEffect keyed on the primitive status/confidence, so it never loops.
     */
    readonly onMatchStateChange?: (matchState: PatientMatchState) => void;
    /**
     * AB#31605 (reopen rehydration): a "matched single" apiResult pre-built from
     * the transaction's PERSISTED patient link (buildPersistedPatientMatch). Only
     * used when `preferPersistedMatch` is true. FULL-ONLY - undefined until the
     * linked patient's detail record has loaded (so pre-resolve never freezes a
     * half-loaded record into the form).
     */
    readonly persistedMatch?: PatientMatchApiPayload;
    /**
     * AB#31605 (reopen rehydration): true on a plain reopen (the tx has a saved
     * patient link and NO fresh extraction happened this session). The parent owns
     * this decision because the row can't tell a stale re-fetched AI key from a
     * fresh one. When true the row pre-resolves from `persistedMatch` and SUPPRESSES
     * the automated search (which would otherwise re-search off stale keys); when
     * false the row ignores `persistedMatch` and searches the extraction keys as
     * usual.
     */
    readonly preferPersistedMatch?: boolean;
    /**
     * AB#31605 (reopen rehydration MINOR-A/B): true while a persisted link is
     * EXPECTED but not yet resolved - either transactionDetails hasn't loaded so
     * the link status is unknown (the stale-requestAnalysis-lands-first race), or
     * the link exists and its detail record is still loading. While true the row
     * HOLDS the automated search and shows a transient "matching" state. The
     * parent releases it once the record resolves (-> preferPersistedMatch
     * pre-resolve) OR the load settles with no record (-> normal search/no-match,
     * no wedge).
     */
    readonly awaitingPersistedMatch?: boolean;
    /**
     * AB#35066: per-value grounding (≡) affordance for DOC-SOURCED patient
     * sub-values (only DOB is ever FROM DOC here - MRN/EMPI are DB/manual). The
     * parent passes its RequestAnalysis.fieldGroundings + the same reveal/no-source
     * handlers the scalar Quick Entry rows use; the icon reveals the source page
     * where a grounding coord exists and degrades to the informational toast
     * otherwise (patient sub-value coords await #34112 - clickable-with-intent).
     */
    readonly fieldGroundings?: readonly FieldGrounding[];
    readonly onRevealGrounding?: (grounding: FieldGrounding) => void;
    readonly onGroundingUnavailable?: () => void;
}

/**
 * Patient Info Quick Entry row (Spec #34101 (F1c), badge 1). Row states:
 * awaiting-document / matching / matched(green) / multiple(yellow) /
 * no-match(red) / service-unavailable(red) - see matchState.ts's `status`.
 *
 * Per Lyn O'Connor's 2026-07-17 decision the SEARCH FIELDS are INLINE on the
 * row: when there is no confident single match (noMatch / serviceUnavailable),
 * or when re-searching a matched row via [Edit], the specialist searches from
 * an inline form pre-filled with the AI-extracted values. On a yellow (Multiple)
 * result the filterable candidate GRID (MatchGridModal, AC9 / master-demo-v6
 * Patient Search) opens automatically: FIRST NAME (sortable) | LAST NAME | DOB
 * | EMPI, a per-column filter row, row-select, and "+ Create New Patient".
 * Selecting a row resolves the row to that patient (single/green); "Review
 * matches" reopens the grid. (Earlier this cut carried only a candidate COUNT
 * with no grid - PatientMatchResult.Candidates now supplies the rows.)
 */
const PatientInfoRow: React.FC<PatientInfoRowProps> = (props) => {
    const [apiResult, setApiResult] = useState<PatientMatchApiPayload | undefined>(undefined);
    const [isSearching, setIsSearching] = useState(false);
    const [serviceUnavailable, setServiceUnavailable] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    // Reveals the inline search form on a matched row when the specialist
    // clicks [Edit] to re-search (AC9 - "[Edit] on a matched row lets the
    // specialist re-search").
    const [searchMode, setSearchMode] = useState(false);
    // Opens the create-new patient form (AC9 no-match path).
    const [createOpen, setCreateOpen] = useState(false);
    /**
     * AB#38824: whether THIS opening of the create form carries the document's values, latched at
     * the moment it opens rather than read live.
     *
     * The modal seeds its inputs once, in a lazy `useState` initializer, and never re-reads
     * `fields` - so the VALUES are frozen at mount whatever happens afterwards. The subtitle is
     * not. Reading the rule live would let a form that opened in `awaitingDocument` (pre-filled,
     * correctly) have its subtitle flip to "Enter the new patient's details" the instant the
     * extraction lands and the automated match resolves - a dialog contradicting its own boxes.
     * One latch, set where the form opens, drives both, so they cannot disagree.
     */
    const [createPrefills, setCreatePrefills] = useState(true);
    // Holds the specialist-entered values after "Create New" so the row renders
    // the created patient (name + DOB/MRN/EMPI, blue user-entered badge) instead
    // of an AI match. Cleared whenever a fresh search runs.
    const [created, setCreated] = useState<PatientCreateValues | undefined>(undefined);
    // Selected candidate in the Multiple radio-card list (defaults to the top
    // pre-selected candidate); cleared on a fresh search.
    const [selectedCandidateId, setSelectedCandidateId] = useState<string | number | undefined>(undefined);
    // AB#35087: candidate count when the specialist tie-broke a MULTIPLE result (undefined for a clean single / fresh search).
    const [selectedFromCount, setSelectedFromCount] = useState<number | undefined>(undefined);

    const runSearch = useCallback(async (keys: PatientSearchKeys, opts?: { manual?: boolean }) => {
        setIsSearching(true);
        setServiceUnavailable(false);
        setSearchMode(false);
        setCreated(undefined);
        setSelectedCandidateId(undefined);
        // A new search result is automated again, so the first-name downgrade must apply to it
        // (BLOCKER-1: the latch suppresses the downgrade only for a human-resolved match).
        specialistResolvedRef.current = false;
        setSelectedFromCount(undefined); // AB#35087: fresh search clears any prior tie-break count
        try {
            const result = await props.search(keys);
            setApiResult(result);
            setIsSearching(false);
            // A MANUAL inline Search opens the filterable results grid (classic
            // parity: the grid shows for ANY count, including the empty state).
            // The AUTOMATED match does NOT open the modal - a Multiple result
            // renders INLINE as a pre-selected radio-card list (master-demo-v6),
            // single -> green inline, zero -> inline no-match (AC9).
            if (opts?.manual) {
                setModalOpen(true);
            }
        } catch {
            setApiResult(undefined);
            setIsSearching(false);
            setServiceUnavailable(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.search]);

    // Automated match (AC4): fires once extraction supplies lastName+dob.
    // A document REPLACE re-extracts and changes these props, which re-runs
    // this effect with the new keys and (via runSearch) clears prior state -
    // an APPEND leaves these props unchanged, so this effect does not re-fire
    // (AC14/Business Rule 10) - no extra "clear on replace" bookkeeping needed.
    // AB#31605: SUPPRESSED on a plain reopen (preferPersistedMatch) - the AI keys
    // re-fetched on reopen are STALE, so searching them would re-pick / re-search
    // over the persisted link; the pre-resolve effect below restores the saved
    // patient instead. Once a fresh extraction fires, preferPersistedMatch is
    // false and this searches the new keys as before.
    useEffect(() => {
        // Also held while awaitingPersistedMatch (AB#31605 MINOR-A): on reopen the
        // stale re-fetched requestAnalysis can land BEFORE transactionDetails, so
        // the parent holds the search until the persisted-link status is known -
        // otherwise this would fire off stale keys and re-search a saved patient.
        if (!props.preferPersistedMatch && !props.awaitingPersistedMatch && props.extractedLastName && props.extractedDob) {
            runSearch({ lastName: props.extractedLastName, dob: toWireDob(props.extractedDob) });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.extractedLastName, props.extractedDob, props.preferPersistedMatch, props.awaitingPersistedMatch]);

    // Pre-resolve from the transaction's PERSISTED patient link on a plain reopen
    // (AB#31605): when the parent says to prefer the saved link (reopen, no fresh
    // extraction) and the full persisted record has loaded, seed apiResult from it
    // so the row shows the resolved patient without re-searching. persistedMatch is
    // FULL-ONLY (buildPersistedPatientMatch returns undefined until the record
    // loads), so this fires once with complete data and the onSelectPatient
    // write-once effect below writes the form once with complete data. Latched via
    // a ref so a later manual re-search that clears apiResult can't snap the row
    // back to the persisted record. A fresh extraction remounts this row
    // (quickEntryIntakeGeneration), resetting the latch.
    const preResolvedRef = useRef(false);
    /**
     * Review finding on PR !9605 (BLOCKER-1): true once a HUMAN resolved this match - the
     * specialist picked a candidate, or the row pre-resolved from the transaction's persisted
     * patient link. Suppresses the AB#35072 first-name downgrade, which otherwise re-fires on the
     * pick itself and leaves the row permanently in the chooser with the patient never linked.
     * A fresh search clears it (that result is automated again); a fresh extraction remounts the
     * row entirely (quickEntryIntakeGeneration), so no reset is needed for that.
     */
    const specialistResolvedRef = useRef(false);
    useEffect(() => {
        if (preResolvedRef.current) {
            return;
        }
        if (props.preferPersistedMatch && props.persistedMatch && !apiResult && !created) {
            preResolvedRef.current = true;
            // A persisted link was established by a human earlier - it must not be re-litigated
            // against a re-fetched extraction on every reopen (BLOCKER-1, second manifestation).
            specialistResolvedRef.current = true;
            setApiResult(props.persistedMatch);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.preferPersistedMatch, props.persistedMatch]);

    const hasKeys = Boolean(props.extractedLastName && props.extractedDob);
    // Reopen window: the parent prefers the persisted link but the full record
    // hasn't resolved into apiResult yet - show a transient "matching" state
    // rather than flashing the no-match inline search off the suppressed keys.
    const resolvingPersisted = Boolean((props.preferPersistedMatch || props.awaitingPersistedMatch) && !apiResult && !created);
    const matchState: PatientMatchState = hasKeys || apiResult
        // AB#35072: pass the extracted FIRST name as a Differs baseline. The search itself cannot key on
        // it (the endpoint takes lastName + dob + mrn only), so this is what stops a single match whose
        // first name disagrees from being silently auto-pinned - see mapPatientMatchState.
        ? mapPatientMatchState(
            apiResult,
            {
                hasKeys, searched: Boolean(apiResult), isSearching, serviceUnavailable,
                userEntered: Boolean(created),
                specialistResolved: specialistResolvedRef.current,
            },
            { lastName: props.extractedLastName, firstName: props.extractedFirstName })
        : DEFAULT_PATIENT_MATCH_STATE;

    // Report the computed match state up so the parent's badge reflects the
    // REAL result. Keyed on the primitive status/confidence (not the freshly-
    // built matchState object) so a same-status re-render doesn't re-fire this
    // effect and cause a report/setState render loop.
    const { onMatchStateChange } = props;
    useEffect(() => {
        // During the reopen resolve window report a transient "matching" state so
        // the badge doesn't flash NeedsEntry off the suppressed keys (AB#31605).
        onMatchStateChange?.(resolvingPersisted ? { ...DEFAULT_PATIENT_MATCH_STATE, status: "matching" } : matchState);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resolvingPersisted, matchState.status, matchState.confidence, onMatchStateChange]);

    // AC9 (coalesce 2026-07-20 fix): when the row resolves to a confident single
    // EXISTING match (automated green match, or a grid pick - both land in
    // apiResult as matched/Single with a `patient` payload), write that patient's
    // identity into the data-entry form so it survives an unmount and reaches the
    // committed transaction. Create-new is excluded (`created` set -> onCreatePatient
    // already wrote the form). Write-once per resolved identity (empi -> mrn ->
    // name+dob) via a ref so it never loops or clobbers a later manual edit.
    const { onSelectPatient } = props;
    const lastWrittenPatientKeyRef = useRef<string | undefined>(undefined);
    useEffect(() => {
        if (created) {
            return; // create-new path owns the form write
        }
        const p = apiResult?.patient;
        if (!apiResult?.matched || matchState.confidence !== "single" || !p) {
            return;
        }
        // Key off the DB id first (empty-string-safe - see resolvePatientMatchWriteKey):
        // a patient with no EMPI serializes empi:"" which used to collapse the key to ""
        // via `??` and skip this write-once, so patientDataid never persisted.
        const key = resolvePatientMatchWriteKey(apiResult);
        if (!key.trim() || lastWrittenPatientKeyRef.current === key) {
            return;
        }
        lastWrittenPatientKeyRef.current = key;
        // Carry the resolved patient's DB id so the parent can link it to the
        // transaction (patientDataid) at Confirm All. For an auto single match the
        // matched record is candidates[0] (PatientManager sets Patient = candidates[0]
        // and always populates Candidates); a grid pick re-seeds candidates to just
        // the chosen one (onModalSelect), so candidates[0] is the resolved id in both
        // paths. Undefined only for the create-new path, which routes through
        // onCreatePatient instead.
        onSelectPatient?.({
            firstName: p.contact?.firstName ?? "",
            middleName: p.contact?.middleName ?? "",
            lastName: p.contact?.lastName ?? "",
            dob: toDisplayDob(p.dob) ?? "",
            mrn: p.mrn ?? "",
            empi: p.empi != null ? String(p.empi) : "",
        }, apiResult?.candidates?.[0]?.patientId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiResult, matchState.confidence, created, onSelectPatient]);

    // Inline search-entry is shown when there's no confident single match to
    // display, or when re-searching a matched row via [Edit].
    // awaitingDocument (no extracted keys / intake not loaded) also opens the
    // inline search so a MANDATORY patient can always be entered/searched
    // manually, rather than sitting as a dead placeholder (coalesce 2026-07-20).
    const showInlineSearch =
        matchState.status === "awaitingDocument" || matchState.status === "noMatch" || matchState.status === "serviceUnavailable" || searchMode;

    // "Last Name/MRN" is a single inline field (master-demo-v6 / the proc's
    // @LastNameMRN param) mapped to the lastName search key the automated
    // match uses.
    const onInlineSearch = (values: Readonly<Record<string, string>>) => {
        runSearch({ lastName: values.lastName, dob: toWireDob(values.dob) }, { manual: true });
    };

    // Create New (AC9 no-match path): open the create-new patient form, pre-filled
    // with the AI-extracted values. Closes the results grid if it was open.
    /**
     * AB#38824: opens the create form, deciding ONCE whether it carries the document's values.
     *
     * Latched rather than read live, because the modal seeds its inputs in a lazy initializer and
     * never re-reads `fields` - the VALUES are frozen at mount whatever happens afterwards.
     * Reading the rule live would let a form opened in `awaitingDocument` have its wording change
     * the instant the extraction landed and the automated match resolved, while its boxes stayed
     * exactly as they were.
     *
     * Latched ONLY on a genuine closed -> open transition. When the form is already open,
     * `setCreateOpen(true)` is a no-op and the modal is NOT remounted, so re-latching would move
     * the divergence rather than close it: new wording over old, frozen inputs.
     */
    const openCreateForm = () => {
        if (!createOpen) {
            setCreatePrefills(shouldPrefillCreateForm(matchState));
        }
        setCreateOpen(true);
    };

    const onOpenCreate = () => {
        setModalOpen(false);
        openCreateForm();
    };

    // The create-new form fields mirror the classic add-new-patient form; only
    // first/last/DOB have an AI-extracted source to pre-fill (middle/MRN/EMPI blank).
    //
    // AB#38824: and only when no DB match was found. With a match on the row, the specialist
    // choosing "add new" is deliberately entering someone the database does not hold, so the
    // form opens EMPTY rather than making them clear the extracted name and DOB first. The rule
    // and every status it turns on live in createPrefill.ts. Gating the VALUES here covers all
    // the doors into this form at once - the inline "No Match - Create New" button and the
    // results grid's "+ Create New Patient" both render this one array.
    //
    // Scoped to the CREATE form on purpose: the inline SEARCH keeps its own pre-fill from the
    // same extracted props unconditionally, because seeding a search with what the document
    // said is the opposite of entering a new person - it is how the specialist re-runs the
    // match ([Edit] on a matched row, AB#31605).
    const prefill = (value: string | undefined) => (createPrefills ? value : undefined);

    const createFields: readonly CreateEntityField[] = [
        // First name is REQUIRED at entry to match createQuickEntryPatient's Confirm-All
        // guard (DataEntryDetails.tsx - requires first + last + DOB, same as Classic's
        // add-new-patient). Without this, a modal accepting last+DOB only would resolve
        // the row but then dead-end at Confirm All when the guard rejects the missing
        // first name (reviewer finding, bug-batch-31605 phase 1).
        { name: "firstName", label: "First Name", value: prefill(props.extractedFirstName), required: true, maxLength: QUICK_ENTRY_FIELD_MAX_LENGTH.patientFirstName },
        { name: "middleName", label: "Middle Name", maxLength: QUICK_ENTRY_FIELD_MAX_LENGTH.patientMiddleName },
        { name: "lastName", label: "Last Name", value: prefill(props.extractedLastName), required: true, maxLength: QUICK_ENTRY_FIELD_MAX_LENGTH.patientLastName },
        { name: "dob", label: "Date of Birth", value: prefill(props.extractedDob), required: true, type: "date" },
        { name: "mrn", label: "MRN", maxLength: QUICK_ENTRY_FIELD_MAX_LENGTH.patientMrn },
        { name: "empi", label: "EMPI", maxLength: QUICK_ENTRY_FIELD_MAX_LENGTH.patientEmpi },
    ];

    /**
     * AB#38824: whether the form the specialist is looking at actually carries anything from the
     * document. Read off the FIELDS, not off the rule, so the wording cannot describe a form it is
     * not attached to. Two cases where the rule alone would lie: `awaitingDocument` pre-fills
     * "vacuously" - permitted, but there is no extraction, so every box is empty and "review the
     * details captured from the document" is nonsense; and a partial extraction is still a
     * pre-fill worth reviewing.
     */
    const carriesDocumentValues = createFields.some(f => (f.value ?? "").trim() !== "");


    const onCreateSubmit = (values: Readonly<Record<string, string>>) => {
        const entered: PatientCreateValues = {
            firstName: values.firstName ?? "",
            middleName: values.middleName ?? "",
            lastName: values.lastName ?? "",
            dob: values.dob ?? "",
            mrn: values.mrn ?? "",
            empi: values.empi ?? "",
        };
        props.onCreatePatient?.(entered);
        // Resolve the row to the newly-entered patient. `created` drives the rich
        // display (name + DOB/MRN/EMPI + source pills) and the blue user-entered
        // badge (userEntered signal); apiResult flips the row out of no-match.
        // No DB write here - the transaction's final Save persists the values.
        // Bug fix (AB#35068): a prior search attempt may have left
        // serviceUnavailable=true (only runSearch clears it, and deriveStatus
        // checks it BEFORE confidence) - mirrors RequestorInfoRow.onCreateSubmit's
        // own fix for this exact failure mode. Without this the row stays stuck
        // showing the service-unavailable/inline-search prompt instead of the
        // just-created patient, and DataEntryDetails.isPatientRequestorConfirmed()
        // (which requires status === "matched") keeps blocking Confirm-All.
        setServiceUnavailable(false);
        // Bug fix (AB#35068, second manifestation): the row is resolved now, so drop
        // the inline search box. Without this, a create started from an [Edit]-opened
        // search (or the no-match path) saved fine but the row kept showing the SEARCH
        // INPUT instead of the new patient - searchMode is sticky (showInlineSearch ORs
        // it in regardless of matchState.status) and outranks the resolved display.
        // Mirrors RequestorInfoRow.onCreateSubmit's identical fix.
        setSearchMode(false);
        setCreated(entered);
        setApiResult({
            matched: true,
            matchMethod: "LastNameDob",
            matchCount: 1,
            confidence: "Single",
            patient: { contact: { lastName: entered.lastName } },
        });
        setCreateOpen(false);
    };

    // "Search manually" (Multiple state): open the inline search over the grid so
    // the specialist can re-run with typed keys instead of picking a candidate.
    const onSearchManually = () => {
        setModalOpen(false);
        setSearchMode(true);
    };

    // Cancel/revert: dismiss the inline search WITHOUT running a search. apiResult
    // (and its candidates) is never cleared, so we drop back to the prior display -
    // the matched-single value, or, for a Multiple result, the candidate grid with
    // the set intact (no auto-pick - AC6 / Business Rule 5).
    const onInlineCancel = () => {
        // Just close the inline search - the row falls back to its prior display:
        // the matched-single value, or the Multiple inline card list (re-rendered
        // automatically once searchMode is false; the candidate set is preserved).
        setSearchMode(false);
    };

    const onModalSelect = (patientId: string | number) => {
        const selected = apiResult?.candidates?.find(candidate => candidate.patientId === patientId);
        if (selected) {
            // AB#35087: remember how many were returned when the specialist tie-broke
            // a MULTIPLE result (before we flatten to the single pick) so the info
            // tooltip reads "N returned — you selected this one".
            const returnedCount = apiResult?.candidates?.length ?? apiResult?.matchCount ?? 0;
            setSelectedFromCount(returnedCount > 1 ? returnedCount : undefined);
            // The specialist has decided: stop the first-name downgrade from overriding them.
            specialistResolvedRef.current = true;
            // Carry the full candidate into the resolved single match so the rich
            // matched display (name + DOB/MRN/EMPI) is complete.
            setApiResult({
                matched: true,
                matchMethod: "LastNameDob",
                matchCount: 1,
                confidence: "Single",
                patient: {
                    contact: { firstName: selected.firstName, lastName: selected.lastName },
                    dob: selected.dob,
                    mrn: selected.mrn,
                    empi: selected.empi,
                },
                // Re-seed candidates to just the picked record so the onSelectPatient
                // effect resolves candidates[0].patientId to THIS chosen patient's id
                // (not the original grid's first row) for the transaction link.
                candidates: [selected],
            });
        }
        setModalOpen(false);
    };


    // Patient Search grid (master-demo-v6): FIRST NAME (sortable) | LAST NAME |
    // DOB | EMPI. Rows come from PatientMatchResult.Candidates (AC9).
    const gridColumns: readonly MatchGridColumn[] = [
        { id: "firstName", label: "FIRST NAME", sortable: true },
        { id: "lastName", label: "LAST NAME" },
        // AB#37980: the DOB column's filter tolerates date separators, so typed digits
        // (10221996) match the 10/22/1996 the cell renders. The only date column in either grid.
        { id: "dob", label: "DOB", filterKind: "date" },
        { id: "empi", label: "EMPI" },
    ];

    const gridRows: readonly MatchGridRow[] = (apiResult?.candidates ?? []).map(candidate => ({
        id: candidate.patientId,
        values: {
            firstName: candidate.firstName ?? "",
            lastName: candidate.lastName ?? "",
            dob: toDisplayDob(candidate.dob) ?? "",
            empi: candidate.empi != null ? String(candidate.empi) : "",
        },
    }));

    const hasCandidates = gridRows.length > 0;

    // Source provenance pill next to a field value (master-demo-v6): a created
    // field prefilled from the extracted document reads FROM DOC; one the
    // specialist typed reads MANUAL. (FROM DATABASE is used for matched DB
    // records - added with the matched rich display.)
    const renderSourcePill = (source?: "doc" | "manual" | "database") => {
        if (!source) {
            return null;
        }
        const label = source === "doc" ? "FROM DOC" : source === "database" ? "FROM DATABASE" : "MANUAL";
        return <span className={`patient-info-row__source-pill patient-info-row__source-pill--${source}`}>{label}</span>;
    };

    // AB#35066: the ≡ grounding icon, shown ONLY for a FROM DOC value (matches the
    // v6 mockup - DOB carries it, FROM DATABASE/MANUAL values do not). Rendered
    // just before the source pill; wired to the parent's reveal/no-source handlers.
    const renderGroundingForSource = (source: "doc" | "manual" | "database" | undefined, field: string, label: string) => {
        if (source !== "doc") {
            return null;
        }
        return (
            <QuickEntryGroundingIcon
                field={field}
                label={label}
                fieldGroundings={props.fieldGroundings}
                onReveal={props.onRevealGrounding}
                onUnavailable={props.onGroundingUnavailable}
            />
        );
    };

    // Rich created-patient display (master-demo-v6): full name + a "New patient"
    // note (blue) + DOB/MRN/EMPI rows with source pills.
    const renderCreatedPatient = (c: PatientCreateValues) => {
        const fullName = [c.firstName, c.middleName, c.lastName].filter(Boolean).join(" ");
        const dobSource: "doc" | "manual" | undefined = c.dob ? (c.dob === props.extractedDob ? "doc" : "manual") : undefined;
        return (
            <div className="patient-info-row__detail">
                <div className="patient-info-row__name">{fullName}</div>
                <div className="patient-info-row__match-note patient-info-row__match-note--new">
                    <span className="patient-info-row__new-icon" aria-hidden="true" /> New patient
                </div>
                <dl className="patient-info-row__fields">
                    <div className="patient-info-row__field">
                        <dt>DOB</dt>
                        <dd>{c.dob}{renderGroundingForSource(dobSource, "patientDateOfBirth", "Patient DOB")}{renderSourcePill(dobSource)}</dd>
                    </div>
                    {c.mrn ? (
                        <div className="patient-info-row__field">
                            <dt>MRN</dt>
                            <dd>{c.mrn}{renderSourcePill("manual")}</dd>
                        </div>
                    ) : null}
                    {c.empi ? (
                        <div className="patient-info-row__field">
                            <dt>EMPI</dt>
                            <dd>{c.empi}{renderSourcePill("manual")}</dd>
                        </div>
                    ) : null}
                </dl>
            </div>
        );
    };

    // Rich matched-patient display (master-demo-v6): full name + a "Matched on..."
    // note + DOB/MRN/EMPI rows. A field we also extracted from the document reads
    // FROM DOC (DOB); a field that only exists on the matched DB record reads FROM
    // DATABASE (MRN/EMPI - the intake extraction has no MRN/EMPI to compare).
    const renderMatchedPatient = () => {
        const p = apiResult?.patient;
        const c = p?.contact;
        const fullName = [c?.firstName, c?.middleName, c?.lastName].filter(Boolean).join(" ") || (props.extractedLastName ?? "");
        const dobDisplay = toDisplayDob(p?.dob?.substring(0, 10)) ?? props.extractedDob;
        // AB#35087: rich "How this match was made" tooltip matching master-demo-v6
        // (search terms + count + tie-break note). The last name / DOB shown are
        // already displayed on this card, so this is on-screen UI to the authorized
        // specialist - not logged (see matchState.buildPatientMatchTooltip).
        const patientTooltip = buildPatientMatchTooltip({
            lastName: c?.lastName ?? props.extractedLastName,
            dob: dobDisplay,
            matchMethod: matchState.matchMethod,
            matchCount: matchState.matchCount,
            selectedFromCount,
        });
        return (
            <div className="patient-info-row__detail">
                <div className="patient-info-row__name">{fullName}</div>
                <div className="patient-info-row__match-note">
                    <span className="patient-info-row__check-icon" aria-hidden="true" />
                    Matched on last name + DOB — single result
                    {/* AB#35087: the "i" icon previously carried only a native title="AI-matched"
                        (aria-hidden, no real tooltip on hover) - wrap it in the app's LightTooltip
                        (hover + keyboard-focus, MUI) showing the rich match metadata, and make it
                        focusable/accessible instead of aria-hidden. */}
                    <MatchInfoTooltip title={<MatchTooltipBody content={patientTooltip} />} placement="top">
                        <span className="patient-info-row__info-icon" tabIndex={0} role="img" aria-label={flattenMatchTooltip(patientTooltip)}>&#9432;</span>
                    </MatchInfoTooltip>
                </div>
                <dl className="patient-info-row__fields">
                    {dobDisplay ? (
                        <div className="patient-info-row__field">
                            <dt>DOB</dt>
                            <dd>{dobDisplay}{renderGroundingForSource("doc", "patientDateOfBirth", "Patient DOB")}{renderSourcePill("doc")}</dd>
                        </div>
                    ) : null}
                    {p?.mrn ? (
                        <div className="patient-info-row__field">
                            <dt>MRN</dt>
                            <dd>{p.mrn}{renderSourcePill("database")}</dd>
                        </div>
                    ) : null}
                    {p?.empi != null ? (
                        <div className="patient-info-row__field">
                            <dt>EMPI</dt>
                            <dd>{String(p.empi)}{renderSourcePill("database")}</dd>
                        </div>
                    ) : null}
                </dl>
            </div>
        );
    };

    // Multiple (yellow): inline radio-card list (master-demo-v6) - the top
    // candidate is pre-selected, an explanatory note above, "Confirm patient" +
    // "search manually" below. (This replaces the earlier modal-grid-on-Multiple;
    // the modal grid is now only the MANUAL search path.)
    const renderMultiplePatient = () => {
        const cands = apiResult?.candidates ?? [];
        // Bug fix (AB#35072, review finding): when the ONLY reason this row is showing a chooser is
        // that the record's first name disagrees with the extraction, two things here were actively
        // harmful. The note asserted the records "share this name" - false in exactly this case -
        // and the sole candidate was pre-selected with a "most complete record" endorsement, putting
        // the wrong patient one click from being attached with the UI vouching for it. Stopping the
        // silent auto-pin (the confidence gate) was necessary but not sufficient: the specialist has
        // to be TOLD what disagrees, and nothing may be pre-selected for them.
        const firstNameDiffers = matchState.differs.firstName;
        const effectiveId = firstNameDiffers ? selectedCandidateId : (selectedCandidateId ?? cands[0]?.patientId);
        return (
            <div className="patient-info-row__multiple">
                <div className="patient-info-row__multiple-note">
                    {firstNameDiffers
                        ? `The date of birth and last name match, but the first name on ${cands.length === 1 ? "this record" : "these records"} differs from the request document${props.extractedFirstName ? ` ("${props.extractedFirstName}")` : ""}. Nothing is pre-selected — check the first name and choose deliberately, or search manually.`
                        : `${cands.length} patient records share this name and date of birth. The top option is pre-selected — the most complete record with the most recent activity.`}
                </div>
                <div className="patient-info-row__cards">
                    {cands.map(c => {
                        const name = [c.firstName, c.lastName].filter(Boolean).join(" ");
                        const selected = c.patientId === effectiveId;
                        return (
                            <label key={c.patientId} className={`patient-info-row__card${selected ? " patient-info-row__card--selected" : ""}`}>
                                <input
                                    type="radio"
                                    name={`patient-candidate-${PATIENT_INFO_ROW_ID}`}
                                    checked={selected}
                                    onChange={() => setSelectedCandidateId(c.patientId)}
                                />
                                <span className="patient-info-row__card-body">
                                    <span className="patient-info-row__card-name">{name}</span>
                                    <span className="patient-info-row__card-meta">
                                        <span>DOB {toDisplayDob(c.dob)}</span>
                                        {c.mrn ? <span>MRN {c.mrn}</span> : null}
                                        {c.empi != null ? <span>EMPI {c.empi}</span> : null}
                                    </span>
                                </span>
                            </label>
                        );
                    })}
                </div>
                <div className="patient-info-row__multiple-actions">
                    {/* Disabled rather than a silent no-op: with nothing pre-selected (the
                        first-name-disagreement case above) the click already did nothing, which
                        reads as a broken button instead of "choose a record first" (AB#35072). */}
                    <button
                        type="button"
                        className="btn patient-info-row__confirm-btn"
                        disabled={effectiveId == null}
                        title={effectiveId == null ? "Select a record first" : undefined}
                        onClick={() => effectiveId != null && onModalSelect(effectiveId)}
                    >
                        ✓ Confirm patient
                    </button>
                    <button type="button" className="patient-info-row__search-manually-link" onClick={onSearchManually}>
                        or search manually
                    </button>
                </div>
            </div>
        );
    };

    const renderValue = () => {
        // NOTE: awaitingDocument is intentionally absent - that status always
        // routes to the inline search (showInlineSearch), so renderValue is never
        // called for it. The old per-row "Awaiting document upload" placeholder was
        // deliberately dropped (coalesce 2026-07-20); the empty-intake message is now
        // the pane-level "Add a request document" panel in DataEntryDetails.
        switch (matchState.status) {
            case "matching":
                return <span className="patient-info-row__matching">Matching...</span>;
            case "matched":
                if (created) {
                    return renderCreatedPatient(created);
                }
                if (matchState.confidence === "single") {
                    return renderMatchedPatient();
                }
                if (matchState.confidence === "multiple" && hasCandidates) {
                    return renderMultiplePatient();
                }
                return (
                    <span className={`patient-info-row__confidence patient-info-row__confidence--${matchState.confidence}`}>
                        {apiResult?.patient?.contact?.lastName ?? props.extractedLastName}
                        {matchState.differs.lastName ? <span className="patient-info-row__differs"> (Differs from record)</span> : null}
                    </span>
                );
            default:
                return null;
        }
    };

    return (
        <div className="qe-row__body patient-info-row" data-match-status={matchState.status}>
            <div className="qe-row__label">Patient Info</div>
            <div className="qe-row__value">
                {resolvingPersisted ? (
                    <span className="patient-info-row__matching">Matching...</span>
                ) : showInlineSearch ? (
                    <div className="patient-info-row__search">
                        {matchState.status === "noMatch" ? (
                            <span className="patient-info-row__no-match">No match found</span>
                        ) : null}
                        {matchState.status === "serviceUnavailable" ? (
                            <span className="patient-info-row__service-unavailable">Match service unavailable</span>
                        ) : null}
                        <InlineSearchForm
                            key={`${props.extractedLastName ?? ""}|${props.extractedDob ?? ""}`}
                            className="patient-info-row__inline-search"
                            fields={[
                                { name: "lastName", label: "Last Name/MRN", value: props.extractedLastName, tag: "REQUIRED", maxLength: PATIENT_LAST_NAME_OR_MRN_MAX_LENGTH },
                                { name: "dob", label: "Date of Birth", value: props.extractedDob, tag: "REQUIRED", type: "date" },
                            ]}
                            onSearch={onInlineSearch}
                            onCancel={searchMode ? onInlineCancel : undefined}
                        />
                        <button
                            type="button"
                            className="qe-btn patient-info-row__inline-no-match-btn"
                            onClick={onOpenCreate}
                        >
                            No Match - Create New
                        </button>
                        {matchState.status === "serviceUnavailable" ? (
                            <span className="patient-info-row__classic-mode-offer">Switch to Classic Mode?</span>
                        ) : null}
                    </div>
                ) : (
                    renderValue()
                )}
            </div>
            {matchState.status === "matched" && matchState.confidence === "single" && !searchMode ? (
                <div className="qe-row__action">
                    <button type="button" className="qe-btn patient-info-row__edit-btn" onClick={() => setSearchMode(true)}>
                        Edit
                    </button>
                </div>
            ) : null}
            {modalOpen ? (
                <MatchGridModal
                    title="Patient Search"
                    subtitle="Select a matching patient to use for this request, or click Create New Patient to add one."
                    columns={gridColumns}
                    rows={gridRows}
                    onSelect={onModalSelect}
                    createNewLabel="+ Create New Patient"
                    onCreateNew={onOpenCreate}
                    onClose={() => setModalOpen(false)}
                />
            ) : null}
            {createOpen ? (
                <CreateEntityFormModal
                    title="Create New Patient"
                    // AB#38824: the subtitle has to agree with what the form actually shows. It
                    // promised "the details captured from the document" unconditionally, which
                    // reads as a fault when the form deliberately opens blank on a matched row.
                    subtitle={carriesDocumentValues
                        ? "Review the details captured from the document, edit as needed, then Save. The patient is saved with the transaction."
                        : "Enter the new patient's details, then Save. The patient is saved with the transaction."}
                    fields={createFields}
                    submitLabel="Save Patient"
                    onSubmit={onCreateSubmit}
                    onClose={() => setCreateOpen(false)}
                />
            ) : null}
        </div>
    );
};

export default PatientInfoRow;

/** Maps the current match state into the generic Quick Entry row-list contract (AC15/AC17). Actual value/confidence/Differs rendering happens in this component's own renderRowContent override, not this generic display. */
export function mapPatientInfoToFieldGroupRow(matchState: PatientMatchState, badgeNumber: number): FieldGroupRow {
    let status: FieldGroupRowStatus;
    switch (matchState.status) {
        case "awaitingDocument":
            status = FieldGroupRowStatus.EmptyNotRequired;
            break;
        case "matching":
            status = FieldGroupRowStatus.LowConfidence;
            break;
        case "matched":
            status = matchState.confidence === "single" ? FieldGroupRowStatus.Confirmed : FieldGroupRowStatus.LowConfidence;
            break;
        default:
            status = FieldGroupRowStatus.NeedsEntry;
            break;
    }

    return {
        id: PATIENT_INFO_ROW_ID,
        badgeNumber,
        status,
        label: "Patient Info",
        value: null,
        // A created/user-entered value gets the blue "user" badge (icon-user),
        // not the green AI-confirmed check (QuickEntryFieldGroupList AC).
        isAiPreSelected: matchState.matched && !matchState.userEntered,
    };
}
